<script setup>
import { ref, computed } from 'vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  time: {
    type: String,
    default: '',
  },
  status: {
    type: String,
    default: '',
  },
  isOutgoing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['contextmenu', 'save-sticker', 'click-item']);

const showGallery = ref(false);
const activeGalleryAttachment = ref({});

const allGalleryAttachments = computed(() => {
  return props.items.map(item => ({
    id: item.attachmentId || item.id,
    message_id: item.messageId || item.id,
    file_type: 'image',
    data_url: item.url,
    file_url: item.url,
    thumb_url: item.url,
  }));
});

const openGallery = (item, index) => {
  activeGalleryAttachment.value = allGalleryAttachments.value[index] || allGalleryAttachments.value[0];
  showGallery.value = true;
  emit('click-item', item);
};

const displayItems = computed(() => {
  if (props.items.length <= 4) {
    return props.items;
  }
  return props.items.slice(0, 4);
});

const remainingCount = computed(() => {
  return Math.max(0, props.items.length - 4);
});
</script>

<template>
  <div class="relative rounded-2xl overflow-hidden shadow-md max-w-[340px] sm:max-w-[380px] select-none bg-black/20 border border-slate-750/30 group/album">
    <!-- 2 Imagens: Lado a Lado (2 colunas) -->
    <div v-if="items.length === 2" class="grid grid-cols-2 gap-1 p-1">
      <div
        v-for="(item, idx) in items"
        :key="item.id || idx"
        class="relative aspect-square overflow-hidden rounded-xl bg-slate-900 cursor-pointer group/item"
        @click="openGallery(item, idx)"
        @contextmenu.prevent="$emit('contextmenu', $event, item.message || item)"
      >
        <img
          :src="item.url"
          class="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
          alt="Foto do álbum"
          loading="lazy"
        />
      </div>
    </div>

    <!-- 3 Imagens: 1 Grande em cima + 2 Menores embaixo (Estilo WhatsApp) -->
    <div v-else-if="items.length === 3" class="grid grid-cols-2 gap-1 p-1">
      <!-- Foto Principal Superior -->
      <div
        class="col-span-2 aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 cursor-pointer group/item"
        @click="openGallery(items[0], 0)"
        @contextmenu.prevent="$emit('contextmenu', $event, items[0].message || items[0])"
      >
        <img
          :src="items[0].url"
          class="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
          alt="Foto principal do álbum"
          loading="lazy"
        />
      </div>

      <!-- 2 Fotos Inferiores Lado a Lado -->
      <div
        v-for="(item, idx) in items.slice(1, 3)"
        :key="item.id || idx"
        class="aspect-square overflow-hidden rounded-xl bg-slate-900 cursor-pointer group/item"
        @click="openGallery(item, idx + 1)"
        @contextmenu.prevent="$emit('contextmenu', $event, item.message || item)"
      >
        <img
          :src="item.url"
          class="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
          alt="Foto do álbum"
          loading="lazy"
        />
      </div>
    </div>

    <!-- 4 ou Mais Imagens: Grade 2x2 -->
    <div v-else class="grid grid-cols-2 gap-1 p-1">
      <div
        v-for="(item, idx) in displayItems"
        :key="item.id || idx"
        class="relative aspect-square overflow-hidden rounded-xl bg-slate-900 cursor-pointer group/item"
        @click="openGallery(item, idx)"
        @contextmenu.prevent="$emit('contextmenu', $event, item.message || item)"
      >
        <img
          :src="item.url"
          class="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
          alt="Foto do álbum"
          loading="lazy"
        />

        <!-- Overlay de Fotos Restantes na 4ª Imagem (ex: +3) -->
        <div
          v-if="idx === 3 && remainingCount > 0"
          class="absolute inset-0 bg-black/65 backdrop-blur-[2px] flex items-center justify-center text-white font-extrabold text-2xl shadow-inner hover:bg-black/50 transition-colors"
        >
          <span>+{{ remainingCount }}</span>
        </div>
      </div>
    </div>

    <!-- Badge Discreta de Horário & Status (Estilo WhatsApp Web no canto inferior) -->
    <div class="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-xs text-[10px] text-white font-medium flex items-center gap-1 shadow-sm pointer-events-none">
      <span v-if="time" class="font-mono">{{ time }}</span>
      <span v-if="isOutgoing" class="text-xs">
        <template v-if="status === 'delivered' || status === 'read'">✓✓</template>
        <template v-else-if="status === 'sent'">✓</template>
        <template v-else-if="status === 'failed'">⚠️</template>
        <template v-else>🕒</template>
      </span>
    </div>

    <!-- Modal / Lightbox de Galeria em Tela Cheia -->
    <GalleryView
      v-if="showGallery"
      v-model:show="showGallery"
      :attachment="activeGalleryAttachment"
      :all-attachments="allGalleryAttachments"
      @close="showGallery = false"
    />
  </div>
</template>
